import { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { isSafeUrl } from "./infoLinkIcons";

/**
 * Markdown renderer for Genie replies in the live chat.
 *
 * Genie answers arrive as text that is often *structured* — numbered steps,
 * bold phrases, the occasional link — and dumping that into a
 * `whitespace-pre-wrap` div reads as an unformatted wall. This renders the
 * structure properly while staying subordinate to the chat's type controls:
 * everything is sized in `em` so the user's font-size preset scales the whole
 * message, and list markers pick up the genie's brand ink.
 *
 * User messages stay plain text (they're short, and echoing a visitor's `*`
 * back as italics would be wrong) — this is for the genie side only.
 */

/**
 * Voice-stack replies use single newlines as real line breaks, but markdown
 * collapses a lone `\n` into a space. Convert singles to hard breaks (two
 * trailing spaces) so the author's line structure survives. Blank lines and
 * list runs are untouched — trailing spaces never change block parsing.
 */
const preserveLineBreaks = (text: string): string =>
  text.replace(/([^\n])\n(?!\n)/g, "$1  \n");

interface GenieMessageMarkdownProps {
  text: string;
  fontSize: number;
  lineHeight: number;
  color: string;
  /** Brand ink for list markers + links. Pass the readable-on-white shade. */
  accentColor: string;
  /** Surface tone. "dark" swaps the fixed inks (strong, rules, code chips)
   *  for light-on-dark equivalents — used by immersive dark surfaces. */
  tone?: "light" | "dark";
  /** Reading-measure cap as a Tailwind max-width class. The compact chat
   *  surfaces keep the default; wider surfaces can pass a looser cap. */
  measure?: string;
}

export const GenieMessageMarkdown = ({
  text,
  fontSize,
  lineHeight,
  color,
  accentColor,
  tone = "light",
  measure = "max-w-[62ch]",
}: GenieMessageMarkdownProps) => {
  const prepared = useMemo(() => preserveLineBreaks(text), [text]);
  const dark = tone === "dark";
  const strongInk = dark ? "text-white" : "text-[#222B45]";
  const ruleInk = dark ? "border-white/20" : "border-[#E5EAF0]";
  const mutedInk = dark ? "text-white/60" : "text-[#64748B]";
  const chipBg = dark ? "bg-white/10" : "bg-black/[0.05]";

  return (
    <div
      className={`genie-md ${measure} font-sans [&>*:first-child]:mt-0 [&>*:last-child]:mb-0`}
      style={{
        fontSize,
        lineHeight: `${lineHeight}px`,
        letterSpacing: "-0.006em",
        color,
        ["--genie-md-marker" as string]: accentColor,
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Voice replies shouldn't shout — headings demote to bold lead lines.
          h1: ({ children }) => <p className="mt-[1em] mb-[0.35em] font-semibold text-[1.05em]">{children}</p>,
          h2: ({ children }) => <p className="mt-[1em] mb-[0.35em] font-semibold text-[1.05em]">{children}</p>,
          h3: ({ children }) => <p className="mt-[0.9em] mb-[0.3em] font-semibold">{children}</p>,
          h4: ({ children }) => <p className="mt-[0.9em] mb-[0.3em] font-semibold">{children}</p>,
          p: ({ children }) => <p className="mb-[0.7em] last:mb-0">{children}</p>,
          ul: ({ children }) => <ul className="mb-[0.7em] list-disc space-y-[0.35em] pl-[1.35em] last:mb-0">{children}</ul>,
          ol: ({ children }) => <ol className="mb-[0.7em] list-decimal space-y-[0.35em] pl-[1.35em] last:mb-0">{children}</ol>,
          li: ({ children }) => <li className="pl-[0.15em] [&>p]:mb-[0.35em] [&>p:last-child]:mb-0">{children}</li>,
          strong: ({ children }) => <strong className={`font-semibold ${strongInk}`}>{children}</strong>,
          em: ({ children }) => <em className="italic">{children}</em>,
          hr: () => <hr className={`my-[0.9em] ${ruleInk}`} />,
          blockquote: ({ children }) => (
            <blockquote className={`mb-[0.7em] border-l-2 pl-[0.9em] ${ruleInk} ${mutedInk}`}>
              {children}
            </blockquote>
          ),
          code: ({ children }) => (
            <code className={`rounded px-[0.35em] py-[0.15em] font-mono text-[0.85em] ${chipBg}`}>{children}</code>
          ),
          pre: ({ children }) => (
            <pre className={`mb-[0.7em] overflow-x-auto rounded-lg p-[0.8em] text-[0.85em] last:mb-0 ${chipBg}`}>
              {children}
            </pre>
          ),
          a: ({ children, href }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium underline underline-offset-2"
              style={{ color: accentColor }}
            >
              {children}
            </a>
          ),
          img: ({ src, alt }) =>
            typeof src === "string" && isSafeUrl(src) ? (
              <img
                src={src}
                alt={alt ?? ""}
                loading="lazy"
                referrerPolicy="no-referrer"
                onError={(event) => {
                  event.currentTarget.style.display = "none";
                }}
                className="my-[0.5em] block max-h-60 max-w-full rounded-xl object-contain"
              />
            ) : null,
          table: ({ children }) => (
            <div className="mb-[0.7em] overflow-x-auto last:mb-0">
              <table className="w-full border-collapse text-[0.92em]">{children}</table>
            </div>
          ),
          th: ({ children }) => (
            <th className="border border-[#E5EAF0] bg-[#F9FAFC] px-[0.6em] py-[0.35em] text-left font-semibold">
              {children}
            </th>
          ),
          td: ({ children }) => <td className="border border-[#E5EAF0] px-[0.6em] py-[0.35em]">{children}</td>,
        }}
      >
        {prepared}
      </ReactMarkdown>
    </div>
  );
};
